import React from 'react';

import { SURFACE, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import LensMark from '~/components/brand/LensMark';
import {
  BRAND_COLORS,
  BRAND_DARK_MODE,
  FONT_STACK_SUISSE_INTL,
  FONT_STACK_SUISSE_WORKS,
} from '~/components/brand/brandTokens';

import { renderRMonogramPaths } from '~/pages/AskRichardUnified/chat/RichardMonogram';

export type AnalyticsPageHeaderProps = {
  eyebrow?: string;
  title: string;
  /** 'accent' is Ocean Teal (Sandy Beige in dark mode), matching Business Pulse's plated title.
   *  'ink' (default) leaves colour to the theme, matching Using data and Commercial. */
  titleColor?: 'accent' | 'ink';
  /** Appends a small Lens ring-dot after the title, as Business Pulse's own title does
   *  (HeroBand.tsx:132-140). Not part of the brief's prop list; kept for pixel parity. */
  titleLensStop?: boolean;
  /** Fixed title size in px, for a surface that wants a smaller title than the shared 44px. */
  titleSize?: number;
  /** A muted breadcrumb link above the title, such as "Lenses › Your teams". */
  crumb?: Array<{ label: string; onClick: () => void }>;
  chip?: React.ReactNode;
  subtitle?: string;
  /** One primary action, right-aligned beside the title block (Using data's "Browse Data
   *  Catalogue" button). */
  action?: React.ReactNode;
  backLink?: { label: string; onClick: () => void };
  /** Wraps the header in Business Pulse's white plate. Plain headers (the default) render a
   *  hairline rule beneath instead. */
  plate?: boolean;
  /** Draws the R monogram watermark behind the title. Only meaningful with `plate`. */
  monogram?: boolean;
  /** Extra content below the subtitle, inside the plate when plated. Business Pulse uses this for
   *  its Ask bar: the Ask bar is page content (it has its own data/behaviour), not part of this
   *  header's contract, so it is not a named prop. Not in the brief's prop list; without it the
   *  Ask bar has nowhere to live inside the plate. */
  children?: React.ReactNode;
};

/**
 * The one page-header shape for the Lens editorial surfaces (Business Pulse, Using data, the Data
 * Catalogue, Commercial). `plate=false` (default): optional back link, Suisse Works title with an
 * optional chip, one-line subtitle, a hairline rule beneath. `plate=true`: the same content inside
 * Business Pulse's white plate, with an optional R monogram watermark. See DESIGN_LANGUAGE.md for
 * every value's source citation.
 */
export default function AnalyticsPageHeader({
  action,
  backLink,
  chip,
  children,
  crumb,
  eyebrow,
  monogram = false,
  plate = false,
  subtitle,
  title,
  titleColor = 'ink',
  titleLensStop = false,
  titleSize,
}: AnalyticsPageHeaderProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const dark = theme.palette.mode === 'dark';
  const plateText = dark ? BRAND_COLORS.sandyBeige : BRAND_COLORS.oceanTeal;
  const titleColorValue = titleColor === 'accent' ? plateText : undefined;

  const eyebrowNode = eyebrow ? (
    <Typography
      sx={{
        color: tokens.accent,
        fontFamily: FONT_STACK_SUISSE_INTL,
        fontSize: 11,
        fontWeight: 650,
        letterSpacing: '.12em',
        textTransform: 'uppercase',
      }}
    >
      {eyebrow}
    </Typography>
  ) : null;

  const crumbNode = crumb?.length ? (
    <Box sx={{ alignItems: 'center', color: 'text.secondary', display: 'flex', fontSize: 12.5, gap: 0.75, mb: 1 }}>
      {crumb.map((link, index) => (
        <React.Fragment key={link.label}>
          {index > 0 && <span aria-hidden>›</span>}
          <ButtonBase
            onClick={link.onClick}
            sx={{
              color: 'inherit',
              fontSize: 'inherit',
              '&:hover': { textDecoration: 'underline' },
              '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: 2 },
            }}
          >
            {link.label}
          </ButtonBase>
        </React.Fragment>
      ))}
    </Box>
  ) : null;

  const titleNode = (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 1.5,
        mt: eyebrow ? 0.5 : 0,
        rowGap: 0.5,
      }}
    >
      <Typography
        component="h1"
        sx={{
          color: titleColorValue,
          fontFamily: FONT_STACK_SUISSE_WORKS,
          fontSize: titleSize ?? (plate ? 38 : { xs: 36, md: 44 }),
          fontWeight: plate ? 400 : undefined,
          letterSpacing: plate ? '-.01em' : '-.03em',
          lineHeight: plate ? 1.05 : 1.08,
          minWidth: 0,
          overflowWrap: 'anywhere',
        }}
      >
        {title}
        {titleLensStop ? (
          <Box
            aria-hidden
            component="span"
            sx={{ display: 'inline-flex', height: 17, ml: '0.08em', transform: 'translateY(0.08em)', width: 17 }}
          >
            <LensMark inverted={dark} size={17} />
          </Box>
        ) : null}
      </Typography>
      {chip ? <Box sx={{ display: 'inline-flex', flex: 'none', transform: 'translateY(-0.12em)' }}>{chip}</Box> : null}
    </Box>
  );

  const backLinkNode = backLink ? (
    <ButtonBase
      aria-label={backLink.label}
      onClick={backLink.onClick}
      sx={{
        alignItems: 'center',
        color: tokens.accent,
        display: 'inline-flex',
        fontSize: 11,
        fontWeight: 650,
        gap: 0.5,
        mb: 1,
        '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: 3 },
      }}
    >
      <ArrowBackIcon aria-hidden sx={{ fontSize: 17 }} />
      {backLink.label}
    </ButtonBase>
  ) : null;

  const subtitleNode = subtitle ? (
    <Typography
      sx={{
        color: plate
          ? dark
            ? alpha(BRAND_COLORS.sandyBeige, 0.85)
            : alpha(BRAND_COLORS.jetBlack, 0.7)
          : 'text.secondary',
        fontFamily: FONT_STACK_SUISSE_INTL,
        fontSize: plate ? 15 : 14.5,
        lineHeight: plate ? 1.4 : 1.45,
        mt: plate ? 1 : 0.5,
      }}
    >
      {subtitle}
    </Typography>
  ) : null;

  if (plate) {
    return (
      <Box
        sx={{
          backgroundColor: tokens.surface,
          border: `1px solid ${dark ? BRAND_DARK_MODE.cardRule : alpha(BRAND_COLORS.oceanTeal, 0.12)}`,
          borderRadius: `${SURFACE.headerPlateRadius}px`,
          overflow: 'hidden',
          px: { xs: 2.5, sm: 3 },
          pt: 2,
          pb: 1.25,
          position: 'relative',
        }}
      >
        {monogram ? (
          <Box
            aria-hidden
            sx={{
              alignItems: 'center',
              bottom: 0,
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              pointerEvents: 'none',
              position: 'absolute',
              right: 0,
              top: 0,
              width: '20%',
            }}
          >
            <Box component="svg" viewBox="0 0 100 100" sx={{ aspectRatio: '1', width: 'clamp(150px, 15vw, 210px)' }}>
              {renderRMonogramPaths(
                dark ? alpha(BRAND_COLORS.compassWhite, 0.075) : alpha(BRAND_COLORS.oceanTeal, 0.075),
              )}
            </Box>
          </Box>
        ) : null}

        <Box sx={{ maxWidth: { xs: '100%', md: '76%' }, minWidth: 0, position: 'relative', zIndex: 1 }}>
          {eyebrowNode}
          {titleNode}
          <Box sx={{ backgroundColor: BRAND_COLORS.oceanTeal, height: 2, mt: 0.875, width: 40 }} />
          {subtitleNode}
          {action ? <Box sx={{ mt: 1.5 }}>{action}</Box> : null}
          {children ? <Box sx={{ mt: 1.25 }}>{children}</Box> : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="header"
      sx={{
        borderBottom: `1px solid ${alpha(tokens.accent, dark ? 0.22 : 0.12)}`,
        pb: { xs: 2.5, md: 3 },
      }}
    >
      {backLinkNode}
      <Box
        sx={{
          alignItems: { md: 'flex-end' },
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: action ? 'minmax(0, 1fr) auto' : '1fr' },
        }}
      >
        <Box sx={{ maxWidth: 760 }}>
          {crumbNode}
          {eyebrowNode}
          {titleNode}
          {subtitleNode}
        </Box>
        {action ? <Box sx={{ justifySelf: { md: 'end' } }}>{action}</Box> : null}
      </Box>
      {children ? <Box sx={{ mt: 2.5 }}>{children}</Box> : null}
    </Box>
  );
}
